import { useState, useMemo } from 'react';
import { useInstitution } from '../../context/InstitutionContext';
import { useRatedInstitutions } from '../../hooks/useInstitutions';
import './InstitutionSelector.css';

interface SelectorState {
  country: string;
  institutionType: string;
  segment: string;
  entityName: string;
  entityId: string;
  searchQuery: string;
}

// Entity type for filtered list
interface Entity {
  id: string;
  name: string;
  country: string;
  type: string;
  segment: string;
}

// Country code to name mapping
const COUNTRY_MAP: Record<string, string> = {
  'Ecuador': 'EC',
  'Colombia': 'CO',
  'Peru': 'PE',
};

// Segment ID to display value mapping (SEG_1 -> S1)
const SEGMENT_MAP: Record<string, string> = {
  'SEG_1': 'S1',
  'SEG_2': 'S2',
  'SEG_3': 'S3',
  'SEG_4': 'S4',
  'SEG_5': 'S5',
  'SEG_MUTUALISTA': 'MUT',
  'SEG_APOYO': 'APO',
};

// Static filter options
const COUNTRIES = [
  { value: 'EC', label: 'Ecuador' },
  { value: 'CO', label: 'Colombia' },
  { value: 'PE', label: 'Peru' },
];

const INSTITUTION_TYPES = [
  { value: 'COOP', label: 'Cooperative' },
  { value: 'MUT', label: 'Mutualista' },
  { value: 'CAJA', label: 'Caja Central' },
];

const SEGMENTS = [
  { value: 'S1', label: 'Segment 1' },
  { value: 'S2', label: 'Segment 2' },
  { value: 'S3', label: 'Segment 3' },
  { value: 'S4', label: 'Segment 4' },
  { value: 'S5', label: 'Segment 5' },
  { value: 'MUT', label: 'Mutualista' },
];

export default function InstitutionSelector() {
  const { selectedInstitution, setSelectedInstitution } = useInstitution();

  // Fetch institutions from BigQuery API
  const { institutions, isLoading } = useRatedInstitutions();

  const [state, setState] = useState<SelectorState>({
    country: '',
    institutionType: '',
    segment: '',
    entityName: '',
    entityId: '',
    searchQuery: '',
  });

  const [showDropdown, setShowDropdown] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Transform API data to entity format
  const entities: Entity[] = useMemo(() => {
    return institutions.map(inst => {
      // Determine institution type from name
      let type = 'COOP';
      if (inst.institution_name.includes('MUTUALISTA')) type = 'MUT';
      else if (inst.institution_name.includes('CAJA CENTRAL')) type = 'CAJA';

      // Create short name
      const shortName = inst.institution_name
        .replace(/^COOPERATIVA DE AHORRO Y CREDITO\s*/i, '')
        .replace(/^ASOCIACION MUTUALISTA DE AHORRO Y CREDITO PARA LA VIVIENDA\s*/i, 'Mutualista ')
        .replace(/^CAJA CENTRAL\s*/i, '')
        .replace(/\s+LTDA\.?$/i, '')
        .replace(/\s+LIMITADA$/i, '')
        .toUpperCase();

      return {
        id: inst.institution_id,
        name: shortName,
        country: COUNTRY_MAP[inst.country] || inst.country,
        type,
        segment: SEGMENT_MAP[inst.segment_id] || inst.segment_id,
      };
    });
  }, [institutions]);

  // Filter entities based on selections and search
  const filteredEntities = useMemo(() => {
    let filtered = entities;

    // Filter by country
    if (state.country) {
      filtered = filtered.filter(e => e.country === state.country);
    }

    // Filter by institution type
    if (state.institutionType) {
      filtered = filtered.filter(e => e.type === state.institutionType);
    }

    // Filter by segment
    if (state.segment) {
      filtered = filtered.filter(e => e.segment === state.segment);
    }

    // Filter by search query
    if (state.searchQuery) {
      const query = state.searchQuery.toLowerCase();
      filtered = filtered.filter(e =>
        e.name.toLowerCase().includes(query) ||
        e.id.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [entities, state.country, state.institutionType, state.segment, state.searchQuery]);

  const handleFilterChange = (field: keyof SelectorState, value: string) => {
    setState(prev => ({
      ...prev,
      [field]: value,
      // Reset entity selection when filters change
      entityName: '',
      entityId: '',
    }));
    setSelectedInstitution(null);
  };

  const handleEntitySelect = (entity: Entity) => {
    setState(prev => ({
      ...prev,
      entityName: entity.name,
      entityId: entity.id,
      searchQuery: entity.name,
    }));
    setSelectedInstitution({
      id: entity.id,
      name: entity.name,
    });
    setShowDropdown(false);
  };

  const handleSearchFocus = () => {
    setShowDropdown(true);
  };

  const handleSearchBlur = () => {
    // Delay to allow click on dropdown items
    setTimeout(() => setShowDropdown(false), 200);
  };

  const handleClearFilters = () => {
    setState({
      country: '',
      institutionType: '',
      segment: '',
      entityName: '',
      entityId: '',
      searchQuery: '',
    });
    setSelectedInstitution(null);
  };

  return (
    <div className="institution-selector">
      {/* Search - Primary input at top */}
      <div className="selector-search-section">
        <div className={`search-wrapper ${selectedInstitution ? 'has-selection' : ''}`}>
          <svg className="search-icon" width="18" height="18" viewBox="0 0 20 20" fill="none">
            <path d="M17.5 17.5L13.875 13.875M15.8333 9.16667C15.8333 12.8486 12.8486 15.8333 9.16667 15.8333C5.48477 15.8333 2.5 12.8486 2.5 9.16667C2.5 5.48477 5.48477 2.5 9.16667 2.5C12.8486 2.5 15.8333 5.48477 15.8333 9.16667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>

          {selectedInstitution ? (
            // Show selected institution as chip inside search bar
            <div className="search-selected-chip">
              <span className="chip-name">{selectedInstitution.name}</span>
              <span className="chip-id">{selectedInstitution.id}</span>
              <button
                onClick={handleClearFilters}
                className="chip-clear-btn"
                aria-label="Clear selection"
                title="Clear selection"
              >
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          ) : (
            // Show search input when nothing selected
            <input
              id="search"
              type="text"
              value={state.searchQuery}
              onChange={(e) => setState(prev => ({ ...prev, searchQuery: e.target.value }))}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
              placeholder="Search institution by name or ID..."
              className="selector-search"
            />
          )}

          {showDropdown && !selectedInstitution && isLoading && (
            <div className="search-dropdown">
              <div className="search-dropdown-empty">Loading institutions...</div>
            </div>
          )}
          {showDropdown && !selectedInstitution && !isLoading && filteredEntities.length > 0 && (
            <div className="search-dropdown">
              {filteredEntities.map(entity => (
                <div
                  key={entity.id}
                  className="search-dropdown-item"
                  onClick={() => handleEntitySelect(entity)}
                >
                  <div className="entity-name">{entity.name}</div>
                  <div className="entity-id">{entity.id}</div>
                </div>
              ))}
            </div>
          )}
          {showDropdown && !selectedInstitution && !isLoading && filteredEntities.length === 0 && (
            <div className="search-dropdown">
              <div className="search-dropdown-empty">No entities found</div>
            </div>
          )}
        </div>

        {/* Filter toggle button */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="filter-toggle-btn"
        >
          {showFilters ? 'Hide Filters' : 'Filters'}
        </button>
      </div>

      {/* Collapsible Filters Drawer */}
      <div className={`filters-drawer ${showFilters ? 'open' : 'closed'}`}>
        <div className="filters-drawer-content">
          <div className="selector-filters">
            {/* Country */}
            <div className="selector-field">
              <label htmlFor="country">Country</label>
              <select
                id="country"
                value={state.country}
                onChange={(e) => handleFilterChange('country', e.target.value)}
                className="selector-dropdown"
              >
                <option value="">All Countries</option>
                {COUNTRIES.map(country => (
                  <option key={country.value} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Institution Type */}
            <div className="selector-field">
              <label htmlFor="institutionType">Institution Type</label>
              <select
                id="institutionType"
                value={state.institutionType}
                onChange={(e) => handleFilterChange('institutionType', e.target.value)}
                className="selector-dropdown"
              >
                <option value="">All Types</option>
                {INSTITUTION_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Segment */}
            <div className="selector-field">
              <label htmlFor="segment">Segment</label>
              <select
                id="segment"
                value={state.segment}
                onChange={(e) => handleFilterChange('segment', e.target.value)}
                className="selector-dropdown"
              >
                <option value="">All Segments</option>
                {SEGMENTS.map(segment => (
                  <option key={segment.value} value={segment.value}>
                    {segment.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Clear button */}
            {(state.country || state.institutionType || state.segment) && (
              <button onClick={handleClearFilters} className="clear-filters-btn">
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
